<template>
  <VDropdown
    :triggers="['click']"
    :placement="placement"
    :distance="distance"
    :skidding="skidding"
    :shown="isOpen"
    :strategy="strategy"
    :popper-class="popperClass"
    @update:shown="onShownChange"
    class="tw:inline-block">
    <slot name="trigger" :toggleDropdown="toggle" :open="isOpen" />
    <template #popper>
      <div :class="panelClass" :style="{ width }">
        <slot name="dropdown" :closeDropdown="close" :open="isOpen" />
      </div>
    </template>
  </VDropdown>
</template>

<script setup lang="ts">
defineOptions({ name: 'AppPopover' });
// Popover — Tailwind wrapper around floating-vue's VDropdown.
//
// Same slot API as @prospective/lithium's DropDown (#trigger with { open,
// toggleDropdown }, #dropdown with { closeDropdown, open }) plus @open, so
// consumers can swap by import path only.
import { computed, ref } from 'vue';

// Runtime registry of z-index tiers we've already injected a stylesheet rule for.
// The popper is teleported to <body>, escaping any component-scoped CSS, and
// floating-vue's own stylesheet sets `.v-popper__popper { z-index: 10000 }` — so
// to override per-instance we need a real, live CSS rule that matches a class
// we can attach via `popperClass`. Registering by number and reusing keeps the
// stylesheet small (one rule per distinct z value across the whole app).
const zIndexClassCache = new Map<number, string>();

function ensureZIndexClass(zIndex: number): string {
  const cached = zIndexClassCache.get(zIndex);
  if (cached) return cached;
  const cls = `v-popper-z-${zIndex}`;
  const style = document.createElement('style');
  style.dataset.popoverZ = String(zIndex);
  // Selector matches floating-vue's specificity for the base z-index rule
  // (`.v-popper__popper`) plus one extra class, so we win without `!important`.
  style.textContent = `.v-popper__popper.${cls} { z-index: ${zIndex}; }`;
  document.head.appendChild(style);
  zIndexClassCache.set(zIndex, cls);
  return cls;
}

const props = withDefaults(
  defineProps<{
    /**
     * Legacy prop matching lithium's DropDown. `left` → `bottom-start`,
     * `right` → `bottom-end`, `center` → `bottom`. When set, wins over
     * `placement`. Prefer `placement` for anything more specific.
     */
    align?: 'left' | 'right' | 'center';
    /** floating-vue placement, e.g. `bottom-start`, `top-end`, `right`. */
    placement?: string;
    /** Any valid CSS width. `auto` leaves the panel to size itself. */
    width?: string;
    /** Extra classes on the panel — e.g. padding, max-height, overflow. */
    panelClass?: string;
    /**
     * Positioning strategy.
     * - `absolute` (default): positioned relative to the nearest positioned
     *   ancestor. Popper follows the trigger when a scrollable ancestor scrolls.
     * - `fixed`: positioned relative to the viewport. Use when the trigger sits
     *   inside a `position: sticky` or `position: fixed` ancestor (`absolute`
     *   would cause the popper to jump on scroll as the containing block moves).
     *   Trade-off: inside a scrollable div, the popper stays glued to the
     *   viewport rather than travelling with the trigger.
     */
    strategy?: 'absolute' | 'fixed';
    /**
     * Distance (px) between the trigger edge and the popper along the main
     * axis (perpendicular to the trigger). floating-vue's `distance`.
     */
    distance?: number;
    /**
     * Shift (px) along the trigger edge (cross-axis). For `bottom-end`
     * placements a positive value pushes the panel toward the start, which
     * moves the arrow away from the edge of the trigger. floating-vue's
     * `skidding`.
     */
    skidding?: number;
    /**
     * Stacking level for the popper element. Defaults to floating-vue's own
     * `10000` (above almost everything). Set lower — e.g. `20` — when the
     * popover should scroll UNDER a sticky/fixed page element such as the
     * app header at `z-30`. Set higher when hosted inside a modal (Bootstrap
     * modal chrome is at `1055`).
     */
    zIndex?: number;
  }>(),
  {
    align: undefined,
    placement: 'bottom-start',
    width: 'auto',
    panelClass: '',
    strategy: 'absolute',
    distance: 6,
    skidding: 0,
    zIndex: 10000,
  }
);

// Fires each time the panel opens. Matches the legacy PickerPopover event —
// used e.g. to lazy-load dropdown contents on first reveal.
const emit = defineEmits<{
  (event: 'open'): void;
}>();

const isOpen = ref(false);

// Lazily register a class → z-index rule the first time a given tier is used.
// The class rides on the teleported popper via `:popper-class` below.
const popperClass = computed(() => ensureZIndexClass(props.zIndex));

// `align` wins when set, so lithium/DropDown consumers keep working unchanged.
const placement = computed(() => {
  if (props.align === 'right') return 'bottom-end';
  if (props.align === 'center') return 'bottom';
  if (props.align === 'left') return 'bottom-start';
  return props.placement;
});

function onShownChange(shown: boolean) {
  isOpen.value = shown;
  if (shown) emit('open');
}

function toggle() {
  isOpen.value = !isOpen.value;
}

function close() {
  isOpen.value = false;
}
</script>

<style>
.v-popper--theme-dropdown .v-popper__inner {
  background: #fff; /* --color-surface */
  border: 1px solid #edeeef !important; /* --color-border */
  border-radius: 8px; /* --radius-md */
  box-shadow: 0 4px 8px 0 rgb(0 0 0 / 0.16) !important; /* --shadow-card */
  color: inherit;
  /* floating-vue's default is `overflow-y: auto`, which clips popper-children
     that anchor their own overlay (LeadSelect's dropdown, autocomplete panels)
     and paints a scrollbar down the panel edge. Let content overflow; callers
     that need an internal scrollable list pass `panelClass="tw:max-h-72
     tw:overflow-auto"` on the inner wrapper instead. */
  overflow: visible;
}

.v-popper--theme-dropdown .v-popper__inner > div {
  overflow: visible;
}

.v-popper--theme-dropdown .v-popper__arrow-outer {
  border-color: #edeeef !important; /* --color-border */
}

.v-popper--theme-dropdown .v-popper__arrow-inner {
  border-color: #fff; /* --color-surface */
}
</style>
